// src/hooks/useCommunityAPI.js
import { useState, useCallback, useRef } from "react";
import { API } from "../api";

export function useCommunityAPI() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const offsetRef = useRef(0);
  const abortControllerRef = useRef(null);

  const normalizePost = useCallback((post) => ({
    id: post.id,
    content: post.content || "",
    // ✅ FIX: Map 'image_url' or 'image' to 'image_url' for consistency
    image_url: post.image_url || post.image || null,
    authorId: post.author_id || post.user_id,
    authorName: post.author_name || post.author?.name || "Anonymous",
    authorAvatar: post.author_avatar || post.author?.avatar || null,
    likeCount: post.like_count || 0,
    commentCount: post.comment_count || 0,
    hasLiked: post.has_liked || false,
    timestamp: post.created_at || new Date().toISOString(),
  }), []);

  const dedupePosts = (list) => {
    const map = new Map();
    list.forEach((post) => map.set(post.id, post));
    return Array.from(map.values());
  };

  const fetchPosts = useCallback(
    async (reset = false) => {
      if (loading) return;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);
      try {
        const limit = 10;
        if (reset) {
          offsetRef.current = 0;
          setHasMore(true);
        }
        const response = await API.get("/community/posts", {
          params: { skip: offsetRef.current, limit },
          signal: abortControllerRef.current.signal,
        });
        const normalized = response.data.map(normalizePost);
        setPosts((prev) => dedupePosts(reset ? normalized : [...prev, ...normalized]));
        offsetRef.current += normalized.length;
        if (normalized.length < limit) setHasMore(false);
      } catch (err) {
        if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
          setError(err.response?.data?.detail || err.message);
        }
      } finally {
        setLoading(false);
        abortControllerRef.current = null;
      }
    },
    [loading, normalizePost]
  );

  const createPost = useCallback(
    async ({ content, images }) => {
      if (!content?.trim() && (!images || images.length === 0)) {
        throw new Error("Post cannot be empty");
      }

      setLoading(true);
      setError(null);

      try {
        const formData = new FormData();
        formData.append("content", content?.trim() || "");

        // ✅ FIX: Backend expects 'images' (singular key)
        if (images?.length > 0) {
          formData.append("images", images[0]);
        }

        // ✅ FIX: Explicitly set Content-Type to undefined to allow browser to set boundary
        // This overrides any global JSON headers in api.js
        const response = await API.post("/community/posts", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        const normalized = normalizePost(response.data);
        setPosts((prev) => dedupePosts([normalized, ...prev]));
        return normalized;
      } catch (err) {
        console.error("Upload error details:", err.response?.data);
        const msg = err.response?.data?.detail || err.message || "Failed to create post.";
        setError(msg);
        throw new Error(msg);
      } finally {
        setLoading(false);
      }
    },
    [normalizePost]
  );

  return {
    posts,
    loading,
    error,
    hasMore,
    fetchPosts,
    loadMore: () => fetchPosts(false),
    createPost,
  };
}